import {
  NetworkActionsToPopupsMap,
  NetworkTreeAction,
  NetworkV2Analytics,
} from '../../networkAppContainer/helpers/types';
import { TreeNodeData } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { GzNodeDirectorWebComponent } from '../../../ui/gzTree/uiElements/gzNodeDirector';
import { NetworkTreeDataSource } from '../dataProvider/NetworkTreeDataSource';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { NetworkPopUpFactory } from '../../networkPopUps/network.pop-up.factory';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { MegaMenuAction } from '../../networkAppContainer/helpers/megaMenu/types';
import { PopUpRenderData } from '../../networkPopUps/types';
import { NetworkActionsHelperFactory } from '../../networkAppContainer/helpers/actions/network.actions.helper.factory';
import { DeleteActionHelper } from '../../networkAppContainer/helpers/actions/specifics/delete.action.helper';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';

export class FrontEndActionEventHandlers {
  public static renameNode(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>) {
    // We don't set temporaryNode on rename because it's handled differently in GzNodeRenameInputWebComponent.save
    //nodeElement.node.temporaryNode = true;
    (nodeElement.node as TreeNodeData).temporaryState = NetworkTreeAction.RENAME;
    nodeElement.dataSource.deleteTemporaryNodes(nodeElement);
    nodeElement.setState({ rename: true });
    nodeElement.dataSource.setEditState(nodeElement, true);
  }

  public static createCompanyFolder(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>) {
    const parentNode = nodeElement.node;
    const baseName = translateFn('New Company Folder');
    const nodeName = this.getUniqueNodeName(nodeElement, baseName);
    const tempNode: TreeNodeData = {
      nodeId: '',
      nodeName: nodeName,
      temporaryNode: true,
      originalData: { type: Constants.Companies.TYPE_COMPANY_FOLDER },
      temporaryState: NetworkTreeAction.CREATE_COMPANY_FOLDER,
    };
    nodeElement.dataSource.deleteTemporaryNodes(nodeElement);
    if (nodeElement.state?.rename) {
      nodeElement.setState(null);
    }
    (nodeElement.dataSource as NetworkTreeDataSource).attachNodeToContainer(tempNode, parentNode, true);
  }

  public static createNetworkFolder(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>) {
    const parentNode = nodeElement.node;
    const baseName = translateFn('New Group');
    const nodeName = this.getUniqueNodeName(nodeElement, baseName);

    const containersFolderParentTypes = [
      Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS,
      Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
    ];
    const type = containersFolderParentTypes.includes(parentNode.originalData.type)
      ? Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER
      : Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP;

    const tempNode: TreeNodeData = {
      nodeId: '',
      nodeName: nodeName,
      temporaryNode: true,
      originalData: { type: type },
      temporaryState: NetworkTreeAction.CREATE_NETWORK_FOLDER,
    };
    nodeElement.dataSource.deleteTemporaryNodes(nodeElement);
    if (nodeElement.state?.rename) {
      nodeElement.setState(null);
    }
    (nodeElement.dataSource as NetworkTreeDataSource).attachNodeToContainer(tempNode, parentNode, true);
  }

  public static async moveTo(protectedEntity: ProtectedEntity, treeElement: HTMLElement) {
    const action: MegaMenuAction = MegaMenuAction.MOVE_TO;
    const config = NetworkActionsToPopupsMap.get(action);
    if (!config) {
      console.error('No popup type found for action', action);
      return;
    }

    const protectedEntities: Array<ProtectedEntity> = [protectedEntity];
    const popUpSpecificsData: PopUpRenderData = await NetworkActionsHelperFactory.getHelper(action).processAction(
      protectedEntities
    );

    NetworkPopUpFactory.getContent(config.popupType, {
      ...config.extraPopupData,
      targets: protectedEntities,
      specifics: popUpSpecificsData,
      parentElementRef: treeElement,
      showOnlyConfirm: popUpSpecificsData.isError ?? false,
    });

    analytics.trackPageInteraction({
      reference: `${NetworkV2Analytics.TRACKING_NAME}:${NetworkV2Analytics.IDENTIFIER_ACTION_CLICKED}`,
      asset: action,
    });
  }

  public static async assignPolicy(protectedEntity: ProtectedEntity, treeElement: HTMLElement) {
    const action: MegaMenuAction = MegaMenuAction.ASSIGN_POLICY;
    this.defaultActionHandlerForNetworkPopUp(action, protectedEntity, treeElement);
  }

  public static async delete(protectedEntity: ProtectedEntity, treeElement: HTMLElement) {
    await DeleteActionHelper.displayDeletePopUp([protectedEntity], treeElement);
  }

  public static async getFullProtectedEntityForTreeNode(
    nodeElement: GzNodeDirectorWebComponent<TreeNodeData>
  ): Promise<ProtectedEntity> {
    try {
      const protectedEntityId = nodeElement.node.originalData._id;
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.EPS,
        endpoint: 'ProtectedEntitiesEPS',
        method: 'getProtectedEntityById',
        payload: {
          page: 1,
          limit: 0,
          protectedEntityId: protectedEntityId,
        },
      });

      return response.data[0];
    } catch (error) {
      const message = translateFn('Cannot complete the action. Target entities have been deleted.');
      const toastErrorConfig: GzToast = {
        message,
        hasAction: false,
        severity: ToastSeverity.ERROR,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastErrorConfig);
      throw new Error('Unable to get full protected entity');
    }
  }

  public static assignSecurityServers(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>, treeElement: HTMLElement) {
    const action: MegaMenuAction = MegaMenuAction.ASSIGN_SECURITY_SERVERS;
    this.defaultActionHandlerForNetworkPopUp(action, nodeElement.node.originalData as ProtectedEntity, treeElement);
  }

  public static syncWithAwsEc2(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>, treeElement: HTMLElement) {
    const action: MegaMenuAction = MegaMenuAction.SYNC_WITH_EC2;
    this.defaultActionHandlerForNetworkPopUp(action, nodeElement.node.originalData as ProtectedEntity, treeElement);
  }

  private static defaultActionHandlerForNetworkPopUp(
    action: MegaMenuAction,
    protectedEntity: ProtectedEntity,
    treeElement: HTMLElement
  ) {
    const config = NetworkActionsToPopupsMap.get(action);

    if (!config) {
      console.error('No popup type found for action', action);
      return;
    }

    const protectedEntities = [protectedEntity];

    NetworkPopUpFactory.getContent(config.popupType, {
      ...config.extraPopupData,
      targets: protectedEntities,
      parentElementRef: treeElement,
    });

    analytics.trackPageInteraction({
      reference: `${NetworkV2Analytics.TRACKING_NAME}:${NetworkV2Analytics.IDENTIFIER_ACTION_CLICKED}`,
      asset: action,
    });
  }

  /*
   This method generates a folder name which is unique between it's siblings in the tree structure.
   If the baseName is already used, it will append a number in parentheses. Ex: New Folder(2)
   */
  private static getUniqueNodeName(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>, baseName: string): string {
    const parentNode = nodeElement.node;
    const childNodeNames = (parentNode.childNodeIds || []).map(id => nodeElement.nodes.get(id)?.nodeName || '');

    let nodeName = baseName;
    for (let i = 2; childNodeNames.includes(nodeName); i++) {
      nodeName = `${baseName}(${i})`;
    }
    return nodeName;
  }
}
